import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Anchor,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import api from '@/config/api';
import { useAuthStore } from '@/store/auth';

export default function Register() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const form = useForm({
    initialValues: {
      business_name: '',
      contact_name: '',
      email: '',
      phone: '',
      password: '',
    },
    validate: {
      business_name: (v) => (v.trim() ? null : 'Business name is required'),
      email: (v) => (/^\S+@\S+$/.test(v) ? null : 'Valid email is required'),
      password: (v) => (v.length >= 6 ? null : 'Password must be at least 6 characters'),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    setError('');
    try {
      // The server sets the JWT as an httpOnly cookie in the response.
      // No need to store the token in localStorage.
      await api.post('/auth/register', values);

      const { data: me } = await api.get('/auth/me');
      login(me);
      navigate('/pending');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center mih="100vh" bg="gray.0">
      <Container size={480} w="100%">
        <Stack align="center" gap={4}>
          <img src="/logo.png" alt="Standard Produce Co." style={{ height: 120 }} />
        </Stack>
        <Text c="dimmed" size="sm" ta="center" mt={5}>
          Create your customer account
        </Text>

        <Paper shadow="md" p={30} mt={30}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                  {error}
                </Alert>
              )}
              <TextInput
                label="Business Name"
                placeholder="Acme Foods Inc."
                required
                {...form.getInputProps('business_name')}
              />
              <TextInput
                label="Contact Name"
                placeholder="John Doe"
                {...form.getInputProps('contact_name')}
              />
              <TextInput
                label="Email"
                placeholder="you@company.com"
                required
                {...form.getInputProps('email')}
              />
              <TextInput
                label="Phone"
                placeholder="(555) 123-4567"
                {...form.getInputProps('phone')}
              />
              <PasswordInput
                label="Password"
                placeholder="At least 6 characters"
                required
                {...form.getInputProps('password')}
              />
              <Button type="submit" fullWidth loading={loading}>
                Create Account
              </Button>
            </Stack>
          </form>
          <Text c="dimmed" size="sm" ta="center" mt="md">
            Already have an account?{' '}
            <Anchor component={Link} to="/login" size="sm">
              Sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Center>
  );
}
